import React, {useEffect, useState, useRef} from 'react';
import PropTypes from 'prop-types';
import {connect, batch, useSelector} from 'react-redux';
import {bindActionCreators} from 'redux';
import {FmlxToast} from 'fmlx-common-ui';
import PageLayout from '../../components/Layout/PageLayout';
import RuntimeHeader from './Header/RuntimeHeader';
import {RowLayout} from '../../components/Layout/Layout';
import FormattedHTMLMessage from '../../components/FormattedHTMLMessage';
import TaskListContainer from '../ProtocolEditor/TaskList/TaskListContainer';
import VirtualTray from '../ProtocolEditor/VirtualTray/VirtualTray';
import DispenseReportContainer from '../DispenseReport/DispenseReportContainer';
import {find} from '../../lib/objectHelper';
import {SignalRHub, SignalREvent, UserRoleLevel, RuntimeType} from '../../constants/dataEnum';
import {setRuntimeReportVisible, setRuntimeRemainingTime} from '../../stores/actions/runtimeStateAction';
import {abortPauseResponseHandler, resumeResponseHandler} from '../../stores/business/runtimeModalBusiness';
import {checkProtocolAvailability, clearRunningProtocol} from '../../stores/business/protocolManagementBusiness';
import {getReport} from '../../stores/business/reportStateBusiness';
import {saveState} from '../../stores/business/stateBusiness';
import {ProtocolEditorContext} from '../ProtocolEditor/ContextProviders/ProtocolEditorContext';
import {abortActionBatch, closeRuntimeView, pauseProtocol} from '../../stores/business/instrumentBusiness';
import {closeRuntimeViewHandler, reRunRunningProtocol} from '../../stores/business/runtimeBusiness';
import {changeView, showLoading} from '../../stores/actions/appStateAction';
import {showModal, hideModal} from '../../stores/actions/modalStateAction';
import withIntl from '../../lib/withIntl';
import {isRoleLevel} from '../../stores/business/userManagementBusiness';
import {getRuntimeType} from '../../stores/selectors/runtimeSelector';
import {getLastOpenedProtocol} from '../../stores/selectors/protocolSelectors';

function mapStateToProps(state) {
  return {
    signalrState: state.signalrState,
    cardStateCards: state.cardState.cards,
    runtimeState: state.runtimeState,
    availableRoleRights: state.userManagementState.availableRoleRights,
    loggedInUser: state.sessionState.user,
    isTessaraInstrumentState: state.licenseState.isTessaraInstrument,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    appStateActionShowLoading: bindActionCreators(showLoading, dispatch),
    appStateActionChangeView: bindActionCreators(changeView, dispatch),
    instrumentBusinessAbortActionBatch: bindActionCreators(abortActionBatch, dispatch),
    runtimeModalBusinessAbortPauseResponseHandler: bindActionCreators(abortPauseResponseHandler, dispatch),
    runtimeBusinessCloseRuntimeViewHandler: bindActionCreators(closeRuntimeViewHandler, dispatch),
    runtimeModalBusinessResumeHandler: bindActionCreators(resumeResponseHandler, dispatch),
    instrumentBusinessPauseProtocol: bindActionCreators(pauseProtocol, dispatch),
    instrumentBusinessCloseRuntimeView: bindActionCreators(closeRuntimeView, dispatch),
    protocolManagementBusinessClearRunningProtocol: bindActionCreators(clearRunningProtocol, dispatch),
    runtimeBusinessReRunRunningProtocol: bindActionCreators(reRunRunningProtocol, dispatch),
    reportStateBusinessGetReport: bindActionCreators(getReport, dispatch),
    runtimeStateActionSetRuntimeReportVisible: bindActionCreators(setRuntimeReportVisible, dispatch),
    stateBusinessSaveState: bindActionCreators(saveState, dispatch),
    runtimeStateActionSetRuntimeRemainingTime: bindActionCreators(setRuntimeRemainingTime, dispatch),
    modalStateActionShowModal: bindActionCreators(showModal, dispatch),
    modalStateActionHideModal: bindActionCreators(hideModal, dispatch),
    userManagementBusinessIsRoleLevel: bindActionCreators(isRoleLevel, dispatch),
    protocolManagementBusinessCheckProtocolAvailability: bindActionCreators(checkProtocolAvailability, dispatch),
  };
}

function Runtime({
  intl,
  cardStateCards,
  runtimeState,
  availableRoleRights,
  loggedInUser,
  signalrState,
  isTessaraInstrumentState,
  userManagementBusinessIsRoleLevel,
  appStateActionShowLoading,
  instrumentBusinessAbortActionBatch,
  runtimeModalBusinessAbortPauseResponseHandler,
  runtimeModalBusinessResumeHandler,
  runtimeBusinessCloseRuntimeViewHandler,
  instrumentBusinessPauseProtocol,
  instrumentBusinessCloseRuntimeView,
  protocolManagementBusinessClearRunningProtocol,
  runtimeBusinessReRunRunningProtocol,
  reportStateBusinessGetReport,
  runtimeStateActionSetRuntimeReportVisible,
  stateBusinessSaveState,
  runtimeStateActionSetRuntimeRemainingTime,
  modalStateActionShowModal,
  modalStateActionHideModal,
  protocolManagementBusinessCheckProtocolAvailability,
}) {
  const selectedCard = find(cardStateCards, {isSelected: true});
  const {
    protocolName,
    progress,
    remainingTime,
    status,
    dialog,
    reportVisible,
    pauseRemainingTime,
    readOnly,
    userExecutor,
    isTessaraProtocol,
  } = runtimeState || {};
  const {headerText, type} = dialog;

  const [taskNumberRefs, setTaskNumberRefs] = useState({}); // will contains {1: refTaskNumber1, 2: refTaskNumber2, etc }

  const selectedCardRef = useRef();

  const isTessaraSuperUser = userManagementBusinessIsRoleLevel(UserRoleLevel.TESSARA_SUPER_USER);
  const isTessaraRegularUserRunningTessaraProtocol = !isTessaraSuperUser && isTessaraProtocol;

  const userRoleLevel = availableRoleRights.map((e) => e.roleLevel)[0];
  const abortOnly =
    readOnly &&
    (userRoleLevel === UserRoleLevel.SERVICE || userRoleLevel === UserRoleLevel.ADMINISTRATOR || isTessaraSuperUser);

  const runtimeType = useSelector(getRuntimeType);
  const lastOpenedProtocol = useSelector(getLastOpenedProtocol);

  useEffect(() => {
    selectedCardRef.current = selectedCard;
  }, [selectedCard]);

  // eslint-disable-next-line react/jsx-no-constructed-context-values
  const protocolEditorContextValue = {
    taskNumberRefs,
    setTaskNumberRefs,
    selectedCardRef,
  };

  // #region header button action

  const onPause = async () => {
    try {
      await instrumentBusinessPauseProtocol(false);
    } catch (error) {
      console.log('Runtime->onPause', SignalREvent.PAUSE_PROTOCOL, 'error', error);
    }
  };

  const onResume = async () => {
    runtimeModalBusinessResumeHandler();
  };

  // #region abort

  const onAbortWithPause = async () => {
    const usernameRequester = abortOnly ? loggedInUser.username : '';
    try {
      appStateActionShowLoading();
      await instrumentBusinessPauseProtocol(true, usernameRequester);
    } catch (error) {
      console.log('Runtime->onAbortWithPause', SignalREvent.PAUSE_PROTOCOL, 'error', error);
    }
  };

  const onAbort = (isMissingTrayError) => {
    const usernameRequester = abortOnly ? loggedInUser.username : '';
    if (isMissingTrayError) {
      instrumentBusinessAbortActionBatch(usernameRequester);
    } else {
      runtimeModalBusinessAbortPauseResponseHandler(null, null, usernameRequester);
    }
  };

  // #endregion abort

  const onReport = async () => {
    await reportStateBusinessGetReport(() => {
      batch(() => {
        runtimeStateActionSetRuntimeReportVisible(true);
        stateBusinessSaveState();
      });
    });
  };

  const onClose = async () => {
    try {
      const result = await instrumentBusinessCloseRuntimeView();

      if (!result) {
        console.log('Closing runtime view - executor');
        await runtimeBusinessCloseRuntimeViewHandler();
      }

      await protocolManagementBusinessClearRunningProtocol();
    } catch (error) {
      console.log('Runtime->onClose', SignalREvent.CLOSE_RUNTIME_VIEW, 'error', error);
    }
  };

  const onReRun = async () => {
    const isProtocolExist = await protocolManagementBusinessCheckProtocolAvailability(lastOpenedProtocol);

    if (isProtocolExist) {
      modalStateActionShowModal(
        intl.formatMessage({id: 'runtimeNotification.rerun.title'}),
        <FormattedHTMLMessage id="runtimeNotification.rerun.content" />,
        [
          {
            className: 'btn-lg',
            style: 'primary',
            handlerClick: () => {
              modalStateActionHideModal();
              runtimeBusinessReRunRunningProtocol();
            },
            text: intl.formatMessage({id: 'runtimeNotification.rerun.ok'}),
          },
          {
            className: 'btn-lg',
            style: 'secondary',
            handlerClick: () => modalStateActionHideModal(),
            text: intl.formatMessage({id: 'runtimeNotification.rerun.cancel'}),
          },
        ],
        () => modalStateActionHideModal(),
        '',
        true,
      );
    }
  };

  // #endregion header button action

  if (reportVisible) {
    return <DispenseReportContainer />;
  }

  return (
    <ProtocolEditorContext.Provider value={protocolEditorContextValue}>
      <PageLayout>
        <PageLayout.Header>
          <RuntimeHeader
            title={`Executing: ${protocolName}`}
            progress={progress}
            remainingTime={remainingTime}
            status={status} /* onStartClick={onStartClick}  */
            runtimeType={runtimeType}
            onPause={onPause}
            onAbortWithPause={onAbortWithPause}
            onResume={onResume}
            onAbort={onAbort}
            onReport={onReport}
            onClose={onClose}
            instrumentHub={signalrState[SignalRHub.INSTRUMENT]}
            pauseRemainingTime={pauseRemainingTime}
            readOnly={readOnly}
            setRuntimeRemainingTime={runtimeStateActionSetRuntimeRemainingTime}
            onReRun={onReRun}
            userName={userExecutor?.name}
            abortOnly={abortOnly}
            hideReportButton={isTessaraRegularUserRunningTessaraProtocol}
            hideRerunButton={!isTessaraInstrumentState && isTessaraProtocol}
            showRemainingTime={runtimeType !== RuntimeType.CONSOLIDATE_TIPS}
          />
        </PageLayout.Header>
        <PageLayout.Content>
          <RowLayout>
            <VirtualTray
              selectedCard={selectedCard}
              noFooter
              runtimeMode
              hideWellHighlight={isTessaraRegularUserRunningTessaraProtocol}
              hideWasteBin={isTessaraRegularUserRunningTessaraProtocol}
              isTessaraProtocol={isTessaraProtocol}
            />
            {!isTessaraRegularUserRunningTessaraProtocol && <TaskListContainer runtimeMode readOnly={readOnly} />}
          </RowLayout>
        </PageLayout.Content>
        {headerText && (
          <FmlxToast
            open={Boolean(headerText)}
            content={
              <p>
                <FormattedHTMLMessage id={headerText.id} values={headerText.values} />
              </p>
            }
            variant={type}
            hideAction
          />
        )}
      </PageLayout>
    </ProtocolEditorContext.Provider>
  );
}

Runtime.propTypes = {
  intl: PropTypes.object.isRequired,
  signalrState: PropTypes.object.isRequired,
  cardStateCards: PropTypes.object.isRequired,
  runtimeState: PropTypes.object.isRequired,
  availableRoleRights: PropTypes.array.isRequired,
  loggedInUser: PropTypes.object.isRequired,
  isTessaraInstrumentState: PropTypes.object.isRequired,
  appStateActionShowLoading: PropTypes.func.isRequired,
  instrumentBusinessAbortActionBatch: PropTypes.func.isRequired,
  runtimeModalBusinessAbortPauseResponseHandler: PropTypes.func.isRequired,
  runtimeBusinessCloseRuntimeViewHandler: PropTypes.func.isRequired,
  runtimeModalBusinessResumeHandler: PropTypes.func.isRequired,
  instrumentBusinessPauseProtocol: PropTypes.func.isRequired,
  instrumentBusinessCloseRuntimeView: PropTypes.func.isRequired,
  protocolManagementBusinessClearRunningProtocol: PropTypes.func.isRequired,
  runtimeBusinessReRunRunningProtocol: PropTypes.func.isRequired,
  reportStateBusinessGetReport: PropTypes.func.isRequired,
  runtimeStateActionSetRuntimeReportVisible: PropTypes.func.isRequired,
  stateBusinessSaveState: PropTypes.func.isRequired,
  runtimeStateActionSetRuntimeRemainingTime: PropTypes.func.isRequired,
  modalStateActionShowModal: PropTypes.func.isRequired,
  modalStateActionHideModal: PropTypes.func.isRequired,
  userManagementBusinessIsRoleLevel: PropTypes.func.isRequired,
  protocolManagementBusinessCheckProtocolAvailability: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(withIntl(Runtime));
